import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MachineEventType } from "@enums/machineEventType.enum";
import { MaterialType } from "@enums/materialType.enum";
import { PurchaseTypeEnum } from "@enums/purchaseType.enum";
import { ToolOwnershipEnum } from "@enums/toolOwnership.enum";
import { environment } from "@environments/environment";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { CriticalSpare } from "@interfaces/criticalSpare.interface";
import { Client } from "@interfaces/models/client.interface";
import { Part } from "@interfaces/models/part.interface";
import { ProductionTool } from "@interfaces/models/productionTool.interface";
import { ProductionToolEvent } from "@interfaces/models/productionToolEvent.interface";
import { ProductionToolBomItem } from "@interfaces/models/productionToolBomItem.interface";
import { ProductionToolPart } from "@interfaces/models/productionToolPart.interface";
import { Supplier } from "@interfaces/models/supplier.interface";
import { ToolingType } from "@interfaces/models/toolingType.interface";
import { Project } from "@interfaces/project.interface";
import { PurchasableItem } from "@interfaces/purchasableItem.interface";
import { Site } from "@interfaces/site.interface";
import { SupportingDocumentFileLineData } from "@interfaces/supportingDocumentFileLineData.interface";
import { ClientService } from "@services/client.service";
import { CriticalSpareService } from "@services/criticalSpare.service";
import { FileService } from "@services/file.service";
import { PartService } from "@services/part.service";
import { ProcessService } from "@services/process.service";
import { ProductionToolService } from "@services/productionTool.service";
import { ProductionToolEventService } from "@services/productionToolEvent.service";
import { ProductionToolBomItemService } from "@services/productionToolBomItem.service";
import { ProductionToolPartService } from "@services/productionToolPart.service";
import { OperationService } from "@services/operation.service";
import { ProjectService } from "@services/project.service";
import { PurchasableItemService } from "@services/purchasableItem.service";
import { SiteService } from "@services/site.service";
import { SystemParameterService } from "@services/systemParameter.service";
import { RequestForQuoteService } from "@services/requestForQuote.service";
import { RequestForQuoteStatusEnum } from "@enums/requestForQuoteStatus.enum";
import { SupplierPurchaseTypeService } from "@services/supplierPurchaseType.service";
import { SupplierService } from "@services/supplier.service";
import { ToolOrderService } from "@services/toolOrder.service";
import { ToolingTypeService } from "@services/toolingType.service";
import { ToastService } from "angular-toastify";
import { Guid } from "guid-typescript";
import { forkJoin, of } from "rxjs";
import { catchError, filter, take } from "rxjs/operators";
import { ToastApiError } from "@helpers/apiError";

@Component({
	moduleId: module.id,
	selector: "productionTools-id",
	styleUrls: ["productionTools-id.css"],
	templateUrl: "productionTools-id.html"
})
export class ProductionToolsIdAdminComponent {
	billetOperations: { [billetId: number]: any[] } = {};
	billets: any[] = [];
	bomItems: ProductionToolBomItem[] = [];
	bomPurchasableItems: PurchasableItem[] = [];
	clients: Client[] = [];
	createEnabled = true;
	criticalSpares: CriticalSpare[] = [];
	editEnabled = true;
	eventTypes: any;
	events: ProductionToolEvent[] = [];
	firstPartImageUrl = "";
	firstPartNumber = "";
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];	
	images: SupportingDocumentFileLineData[] = [];
	item: ProductionTool = new ProductionTool();
	itemId = 0;
	itemType = "Tool";
	loading = false;
	materialPurchasableItems: PurchasableItem[] = [];
	materialTypes: any;
	ownershipTypes: any;
	parts: Part[] = [];
	popupAddButtonOptions: any = { onClick: () => this.addNewEvent(), text: "Add Event", visible: true };
	popupBomItem: ProductionToolBomItem = new ProductionToolBomItem();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupCriticalSpare: CriticalSpare = new CriticalSpare();
	popupEvent: ProductionToolEvent = new ProductionToolEvent();
	popupImage: SupportingDocumentFileLineData = new SupportingDocumentFileLineData();
	popupProductionToolPart: ProductionToolPart = new ProductionToolPart();
	popupPurchasableItem: PurchasableItem = new PurchasableItem();
	popupRequestQuotes: { bomItemIds: number[]; supplierIds: number[]; toolOrderIds: number[] } = { bomItemIds: [], supplierIds: [], toolOrderIds: [] };
	popupTitle = "Add New Event";
	popupTool: ProductionTool = new ProductionTool();
	popupVisible = false;
	processes: any[] = [];
	productionToolParts: ProductionToolPart[] = [];
	projects: Project[] = [];
	purchasableItems: PurchasableItem[] = [];
	purchaseOrder: SupportingDocumentFileLineData = new SupportingDocumentFileLineData();
	purchaseTypes = GetPropertiesFromEnum(PurchaseTypeEnum);
	readOnly = true;
	requestForQuoteStatuses = GetPropertiesFromEnum(RequestForQuoteStatusEnum);
	requestForQuotes: any[] = [];
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	rfqSuppliers: Supplier[] = [];
	screenMode = "create";
	sites: Site[] = [];
	suppliers: Supplier[] = [];
	title: string = "View " + this.itemType;
	toolingTypes: ToolingType[] = [];

	// Values shown on the header card above the tabs.
	get wizardSelectedPartNumber(): string {
		return (this.parts || []).find((p: any) => p.id === this.wizardPartId)?.partNumber || "";
	}

	get clientName(): string {
		return (this.clients || []).find((c: any) => c.id === this.item?.clientId)?.name || "";
	}

	// Blows used in the current service period, against the warning and critical levels.
	get serviceGauge(): any {
		const current = this.item?.currentServicePeriodBlows || 0;
		const interval = this.item?.serviceIntervalBlows || 0;
		const warning = this.item?.warningLevelBlows || 0;
		const critical = this.item?.criticalLevelBlows || 0;

		if (interval <= 0 && current <= 0) {
			return null;
		}

		// The bar runs to whichever is furthest out, so an overdue tool still shows where it sits.
		const max = Math.max(interval, critical, warning, current, 1);
		const percent = (value: number) => Math.min(100, (value / max) * 100);
		const status = critical > 0 && current >= critical ? "critical" : warning > 0 && current >= warning ? "warning" : "ok";

		return {
			critical,
			criticalPercent: critical > 0 ? percent(critical) : null,
			current,
			interval,
			percent: percent(current),
			remaining: interval > 0 ? interval - current : null,
			status,
			warning,
			warningPercent: warning > 0 ? percent(warning) : null,
		};
	}

	// Blows over the tool's whole life, against what it is expected to last.
	get lifetimeGauge(): any {
		const current = this.item?.lifetimeBlows || 0;
		const expected = this.item?.expectedLifetimeBlows || 0;

		if (expected <= 0 && current <= 0) {
			return null;
		}

		const max = Math.max(expected, current, 1);
		const used = expected > 0 ? (current / expected) * 100 : 0;

		return {
			current,
			expected,
			percent: Math.min(100, (current / max) * 100),
			status: used >= 100 ? "critical" : used >= 90 ? "warning" : "ok",
		};
	}

	get ownershipName(): string {
		return (this.ownershipTypes || []).find((o: any) => o.id === this.item?.ownership)?.name || "";
	}

	get projectName(): string {
		return (this.projects || []).find((p: any) => p.id === this.item?.projectId)?.name || "";
	}

	get storageSummary(): string {
		const site = (this.sites || []).find((x: any) => x.id === this.item?.siteId)?.name;
		const rack = this.item?.rackLocation;
		return [site, rack].filter(Boolean).join(" · ") || "—";
	}

	// An isometric drawing of the tool, split into its lower and upper halves. Projected as
	// x' = (x - z)·cos30, y' = (x + z)·sin30 - y, so the box follows the dimensions entered;
	// an empty tool gets a nominal block so the drawing still reads.
	get toolDiagram(): any {
		const length = this.item?.toolLength > 0 ? this.item.toolLength : 800;
		const width = this.item?.toolWidth > 0 ? this.item.toolWidth : 600;
		const height = this.item?.toolHeight > 0 ? this.item.toolHeight : 400;

		const scale = 150 / Math.max(length, width, height);
		const l = length * scale;
		const w = width * scale;
		const h = height * scale;
		const cos30 = Math.cos(Math.PI / 6);
		const sin30 = 0.5;
		const point = (x: number, z: number, y: number) => `${((x - z) * cos30).toFixed(1)},${((x + z) * sin30 - y).toFixed(1)}`;

		// The three faces of a half you can see from this angle.
		const half = (bottom: number, top: number) => ({
			front: [point(0, w, bottom), point(0, w, top), point(l, w, top), point(l, w, bottom)].join(" "),
			right: [point(l, 0, bottom), point(l, 0, top), point(l, w, top), point(l, w, bottom)].join(" "),
			top: [point(0, 0, top), point(l, 0, top), point(l, w, top), point(0, w, top)].join(" "),
		});

		// Bounds of everything drawn, padded, so the viewBox always frames the box.
		const xs = [-w * cos30, l * cos30, (l - w) * cos30, 0];
		const ys = [-h, (l + w) * sin30, (l + w) * sin30 - h, w * sin30];
		const pad = 18;
		const minX = Math.min(...xs) - pad;
		const minY = Math.min(...ys) - pad;
		const box = [minX, minY, Math.max(...xs) - minX + pad, Math.max(...ys) - minY + pad];

		return {
			lower: half(0, h / 2),
			upper: half(h / 2, h),
			viewBox: box.map((n) => n.toFixed(1)).join(" "),
		};
	}

	get toolingTypeName(): string {
		return (this.toolingTypes || []).find((t: any) => t.id === this.item?.toolingTypeId)?.name || "";
	}

	wizardAddBilletNext = true;
	wizardLastSuggestedName = "";
	wizardPartId: number | null = null;
	wizardPartRouterStage = 1;
	wizardRouterStages: any[] = [];
	wizardStep = 1;
	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: ProductionToolService, private clientService: ClientService, private criticalSpareService: CriticalSpareService, private fileService: FileService, private productionToolEventService: ProductionToolEventService, private productionToolPartService: ProductionToolPartService, private projectService: ProjectService, private purchasableItemService: PurchasableItemService, private siteService: SiteService, private requestForQuoteService: RequestForQuoteService, private supplierPurchaseTypeService: SupplierPurchaseTypeService, private supplierService: SupplierService, private toolingTypeService: ToolingTypeService, private productionToolBomItemService: ProductionToolBomItemService, private toolOrderService: ToolOrderService, private partService: PartService, private processService: ProcessService, private systemParameterService: SystemParameterService, private operationService: OperationService) {
		this.deleteBomItem = this.deleteBomItem.bind(this);
		this.buildButtons("view");
		this.getClients();
		this.getParts();
		this.getProcesses();
		this.getPurchasableItems();
		this.getSites();
		this.getRfqSuppliers();
		this.getSuppliers();
		this.getToolingTypes();
		this.eventTypes = GetPropertiesFromEnum(MachineEventType);
		this.materialTypes = GetPropertiesFromEnum(MaterialType);
		this.ownershipTypes = GetPropertiesFromEnum(ToolOwnershipEnum);

		this.route.params.subscribe((params) => {
			if (params.id != "create") {
				// Angular reuses this component when the wizard navigates from /create to the new tool,
				// so the constructor's view mode never runs again - reset it here or the page stays a wizard.
				this.cancelEditing();
				if (params.id.split("_").length > 1) {
					this.itemId = params.id.split("_")[0];
					this.getItem(this.itemId);
					this.edit();
				} else {
					this.itemId = params.id;
					this.getItem(params.id);
				}
			} else {
				this.create();
				this.route.queryParams.subscribe((queryParams) => {
					if (queryParams.name) this.item.name = queryParams.name;
					if (queryParams.clientId) {
						this.item.clientId = +queryParams.clientId;
						this.getProjects();
					}
					if (queryParams.projectId) this.item.projectId = +queryParams.projectId;
				});
			}
		});
		
		this.deleteCriticalSpare = this.deleteCriticalSpare.bind(this);
		this.deleteImageLink = this.deleteImageLink.bind(this);
		this.deleteEvent = this.deleteEvent.bind(this);
		this.editCriticalSpare = this.editCriticalSpare.bind(this);
		this.editEvent = this.editEvent.bind(this);
		this.uploadEventImage = this.uploadEventImage.bind(this);
		this.uploadImage = this.uploadImage.bind(this);
		this.uploadPurchaseOrder = this.uploadPurchaseOrder.bind(this);
		this.viewEventImage = this.viewEventImage.bind(this);
		this.viewImage = this.viewImage.bind(this);
		this.viewBillet = this.viewBillet.bind(this);
		this.viewRequestForQuote = this.viewRequestForQuote.bind(this);
		this.viewPart = this.viewPart.bind(this);
	}

	
	// Billets are created in the tool order wizard, which picks the tool up from the query string.
	addBillet() {
		this.router.navigate(["admin/toolOrders/create"], { queryParams: { productionToolId: this.item.id } });
	}

	addNewBomItem() {
		if (!this.popupBomItem.purchasableItemId) {
			alert("Choose an item");
			return;
		}
		this.productionToolBomItemService.createSingle(this.popupBomItem)
			.subscribe(
				() => {
					this.closePopup();
					this.popupBomItem = new ProductionToolBomItem();
					this.getBomItems();
					this.toastService.success("Added to the bill of materials");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Could not add the item.");
				}
			);
	}

	addNewBomItemPopup() {
		this.popupBomItem = new ProductionToolBomItem();
		this.popupBomItem.productionToolId = this.item.id;
		this.popupTitle = "Add BOM Item";
		this.popupAddButtonOptions = { onClick: () => this.addNewBomItem(), text: "Add Item", visible: true };
		this.popupVisible = true;
	}

	addNewCriticalSpare() {
		this.criticalSpareService.createSingle(this.popupCriticalSpare)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getCriticalSpares();
					this.toastService.success("Successfully Added Availability");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	addNewCriticalSparePopup() {
		this.popupCriticalSpare = new CriticalSpare();
		this.popupCriticalSpare.productionToolId = this.item.id;
		this.popupTitle = "Add New Critical Spare";
		this.popupAddButtonOptions = { onClick: () => this.addNewCriticalSpare(), text: "Add Spare", visible: true };
		this.popupVisible = true;
	}
	
	addNewEvent() {
		this.productionToolEventService.createSingle(this.popupEvent)
			.subscribe(
				() => {
					this.popupVisible = false;
					if (this.popupEvent.eventType == MachineEventType.Service) {
						this.getItem(this.item.id);
					} else {
						this.getEvents();
					}
					this.popupEvent = new ProductionToolEvent();
					this.toastService.success("Successfully Added Event");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	addNewEventPopup() {
		this.popupEvent = new ProductionToolEvent();
		this.popupEvent.productionToolId = this.item.id;
		this.popupTitle = "Add New Event";
		this.popupAddButtonOptions = { onClick: () => this.addNewEvent(), text: "Add Event", visible: true };
		this.popupVisible = true;
	}

	addNewImage() {		
		this.images.push(this.popupImage);
		this.popupImage = new SupportingDocumentFileLineData();
		this.closePopup();
	}

	addNewImagePopup() {
		this.popupImage = new SupportingDocumentFileLineData();
		this.popupTitle = "Add New Image";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewImage(), text: "Add Image" };
	}
	
	// Linking a part can be done from either side - the part's Production Requirements tab does the same.
	addNewPart() {
		if (!this.popupProductionToolPart.partId) {
			alert("Choose a part");
			return;
		}
		if (!this.popupProductionToolPart.routerStage || this.popupProductionToolPart.routerStage < 1) {
			alert("Enter the router stage this tool is used for");
			return;
		}
		this.productionToolPartService.createSingle(this.popupProductionToolPart)
			.subscribe(
				() => {
					this.closePopup();
					this.popupProductionToolPart = new ProductionToolPart();
					// Reload the tool: linking its first part renames the tool and fills the header card.
					this.getItem(this.item.id);
					this.toastService.success("Part linked to this tool");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Could not link the part - check the router stage isn't already used by another tool for it.");
				}
			);
	}

	addNewPartPopup() {
		this.popupProductionToolPart = new ProductionToolPart();
		this.popupProductionToolPart.productionToolId = this.item.id;
		this.popupProductionToolPart.isActive = true;
		this.popupTitle = "Add Part";
		this.popupAddButtonOptions = { onClick: () => this.addNewPart(), text: "Add Part", visible: true };
		this.popupVisible = true;
	}

	addNewPurchasableItem() {
		if (!this.popupPurchasableItem.name) {
			alert("Give the item a name");
			return;
		}
		this.purchasableItemService.createSingle(this.popupPurchasableItem)
			.subscribe(
				() => {
					this.closePopup();
					this.popupPurchasableItem = new PurchasableItem();
					// Back onto the list the BOM popup picks from, so the new item is there to choose straight away.
					this.getPurchasableItems();
					this.toastService.success("Item created");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Could not create the item.");
				}
			);
	}

	addNewPurchasableItemPopup() {
		this.popupPurchasableItem = new PurchasableItem();
		this.popupPurchasableItem.materialType = MaterialType.NotApplicable;
		this.popupPurchasableItem.purchaseType = PurchaseTypeEnum.CriticalSpares;
		this.popupTitle = "Add New Purchasable Item";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewPurchasableItem(), text: "Add Item" };
	}

	// A BOM line reads as its item and quantity in the picker.
	bomItemLabel(item: any) {
		return item ? `${item.purchasableItem ? item.purchasableItem.name : "Item " + item.purchasableItemId} (x${item.quantity})` : "";
	}

	buildButtons(mode: string) {
		this.screenMode = mode;
		this.headerPrimaryButtons = [];
		this.headerTertiaryButtons = [];
		if (mode == "edit" && this.editEnabled) {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode == "create" && this.createEnabled) {
			// The wizard carries its own Back / Next / Create buttons.
			this.headerPrimaryButtons = [
				{ method: "close", text: "Close" }
			];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
			// Add Spare and Create New Spare live on the Critical Spares tab, next to the grid they fill.
			this.headerTertiaryButtons = [
				{ method: "addNewEventPopup", text: "Add Event" },
				{ icon: "fal fa-calendar-alt", method: "goToToolOrderPlanning", text: "Planning" },
			];
		}
	}

	cancelEditing() {
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	closePopup() {
		this.popupVisible = false;
	}

	create() {
		this.readOnly = false;
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
		// Storage isn't asked for in the wizard, but a tool has to have a site. It is made in the
		// toolroom, so start it there; the Storage tab moves it, and completion still needs a rack.
		this.systemParameterService.getValueByName("ToolroomSettings").subscribe(
			(value: any) => {
				if (!this.item.siteId && value?.ToolroomSiteId) {
					this.item.siteId = Number(value.ToolroomSiteId);
				}
			},
			(err) => console.log("ToolroomSettings SystemParam not configured:", err)
		);
	}

	// Wizard finish: create the tool, link the chosen part, then go on to the billet wizard or the tool.
	createItem() {
		if (!this.wizardPartValid()) {
			this.wizardStep = 1;
			return;
		}

		if (!this.wizardDetailsValid()) {
			this.wizardStep = 2;
			return;
		}

		// A tool has to have a site. create() starts it at the toolroom, but that lookup runs in the
		// background and only logs on failure, so fetch it again here and show why if it can't be had.
		// Recursing only once the site is set means this cannot loop.
		if (!this.item.siteId) {
			this.systemParameterService.getValueByName("ToolroomSettings").subscribe(
				(value: any) => {
					console.log("ToolroomSettings", value);
					const siteId = Number(value?.ToolroomSiteId);

					if (!siteId) {
						this.toastService.error("ToolroomSettings was read, but it has no ToolroomSiteId.");
						return;
					}
					this.item.siteId = siteId;
					this.createItem();
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Could not read the toolroom site: " + (err?.message || err?.error?.error || err?.statusText || "unknown error"));
				}
			);
			return;
		}

		this.item.imageURLs = JSON.stringify(this.images);
		this.item.poURL = JSON.stringify(this.purchaseOrder);
		const enteredName = this.item.name;

		this.itemService.createSingle(this.item)
			.subscribe(
				(res: any) => {
					const toolId = res.response?.id || res.id;

					if (!toolId) {
						this.toastService.error("Tool created but could not be opened - find it in the tools list.");
						this.close();
						return;
					}

					if (!this.wizardPartId) {
						this.finishWizard(toolId);
						return;
					}

					const link = new ProductionToolPart({
						isActive: true,
						partId: this.wizardPartId,
						productionToolId: toolId,
						routerStage: this.wizardPartRouterStage || 1,
					});

					this.productionToolPartService.createSingle(link)
						.subscribe(
							() => {
								// Linking a part renames the tool after it, so put a typed-over name back.
								if (enteredName && enteredName !== this.wizardLastSuggestedName) {
									this.itemService.updateSingleById(toolId, { name: enteredName })
										.subscribe(() => this.finishWizard(toolId), () => this.finishWizard(toolId));
									return;
								}
								this.finishWizard(toolId);
							},
							(err: any) => {
								console.log(err);
								ToastApiError(this.toastService, err, "Tool created, but the part could not be linked - check the router stage isn't already used for it.");
								this.finishWizard(toolId);
							}
						);
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Could not create the tool.");
				}
			);
	}

	deleteBomItem(e: any) {
		if (confirm("Remove this item from the bill of materials?") === false) {
			return false;
		}
		this.productionToolBomItemService.deleteSingleById(e.row.data.id)
			.subscribe(
				() => { this.toastService.success("Item removed"); this.getBomItems(); },
				(err: any) => { console.log(err); ToastApiError(this.toastService, err, "Could not remove the item."); }
			);
		return false;
	}

	deleteCriticalSpare(e: any) {
		if (confirm("Are you sure you wish to delete this critical spare?")) {
			this.criticalSpareService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success("Availability Deleted");
						this.getCriticalSpares();
					},
					(err: any) => console.log(err)
				);
		}
	}

	deleteEvent(e: any) {
		if (confirm("Are you sure you wish to delete this event?")) {
			this.productionToolEventService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success("Event Deleted");
						this.getEvents();
					},
					(err: any) => console.log(err)
				);
		}
	}

	deleteImageLink(e: any) {
		const index = this.images.findIndex(d => d.lookupId === e.row.data.lookupId);
		this.images.splice(index, 1);

		return false;
	}

	doesEventHaveImage(e: any) {
		return (e.row.data.eventImageURL === null || e.row.data.eventImageURL === undefined || e.row.data.eventImageURL === "") === false;
	}

	downloadPurchaseOrder() {
		const lookupId = Guid.parse(this.purchaseOrder.lookupId);
		this.fileService.downloadFile(lookupId).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", this.purchaseOrder.fileName);
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
		return false;
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.warn("You are now editing...");
	}
	
	editCriticalSpare(e: any) {
		const criticalSpareToEdit = this.criticalSpares.find((f)=> f.id == e.row.data.id);
		if (criticalSpareToEdit !== null && criticalSpareToEdit !== undefined) {
			this.popupCriticalSpare = criticalSpareToEdit;
		} else {
			return;
		}
		this.popupTitle = "Edit Critical Spare";
		this.popupAddButtonOptions = { onClick: () => this.updateCriticalSpare(), text: "Save Changes", visible: true };
		this.popupVisible = true;
	}
	
	editEvent(e: any) {
		const eventToEdit = this.events.find((f)=> f.id == e.row.data.id);
		if (eventToEdit !== null && eventToEdit !== undefined) {
			this.popupEvent = eventToEdit;			
		} else {
			return;
		}
		this.popupTitle = "Edit Event";
		this.popupAddButtonOptions = { onClick: () => this.updateEvent(), text: "Save Changes", visible: true };
		this.popupVisible = true;
	}

	// The header card is the tool's identity, so it is edited here rather than duplicated on the
	// Details tab. Site and rack are also on the Storage tab; the card shows them, so they are editable here too.
	editHeaderPopup() {
		this.popupTool = new ProductionTool({ ...this.item });
		this.popupTitle = "Edit Tool Details";
		this.popupAddButtonOptions = { onClick: () => this.saveHeader(), text: "Save", visible: true };
		this.popupVisible = true;
	}

	// Billets (tool orders) are one half of the tool's bill of materials.
	// Mirrors the API's auto-naming so the wizard can show the name the tool will get. The API renames the
	// tool when the part is linked, and the wizard writes the entered name back afterwards if it was changed.
	finishWizard(toolId: number) {
		this.toastService.success("Tool created");
		if (this.wizardAddBilletNext) {
			this.router.navigate(["admin/toolOrders/create"], { queryParams: { productionToolId: toolId } });
			return;
		}
		this.router.navigate([`/admin/productionTools/${toolId}`]);
	}

	// Operations are stored by stage (1, 2, 3); the shop floor reads them as OP10, OP20, OP30.
	formatOpNumber(cellInfo: any) {
		return cellInfo.value ? "OP" + cellInfo.value * 10 : "";
	}

	generatedToolName(): string {
		const part: any = (this.parts || []).find((p: any) => p.id === this.wizardPartId);
		if (!part) {
			return "";
		}
		return `TL-${(part.partNumber || "").trim().replace(/\s+/g, "-")}-001`;
	}

	// Billets, and the operations on each for the billet rows' detail. Operations are fetched per billet up
	// front, because the detail template can't make a request as it renders. One billet failing leaves
	// that billet's operations empty rather than losing the rest.
	getBillets() {
		this.toolOrderService.forProductionTool(this.item.id)
			.subscribe(
				(res: any) => {
					this.billets = res.response || [];
					this.billetOperations = {};

					if (this.billets.length === 0) {
						return;
					}

					forkJoin(this.billets.map((b: any) => this.operationService.forToolOrder(b.id).pipe(catchError(() => of({ response: [] })))))
						.subscribe((results: any[]) => {
							this.billets.forEach((b: any, i: number) => this.billetOperations[b.id] = results[i]?.response || []);
						});
				},
				(err) => console.log(err)
			);
	}

	getBomItems() {
		this.productionToolBomItemService.forProductionToolId(this.item.id)
			.subscribe(
				(res: any) => this.bomItems = res.response || [],
				(err) => console.log(err)
			);
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) => {
					this.clients = res.response.items;
				},
				(err) => {console.log(err); ToastApiError(this.toastService, err);}
			);
	}

	getCriticalSpares() {
		this.criticalSpareService.forProductionToolId(this.item.id)
			.subscribe(
				(res: any) => this.criticalSpares = res.response,
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	getEvents() {
		this.productionToolEventService.forProductionToolId(this.item.id)
			.subscribe(
				(res: any) => this.events = res.response,
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	getItem(itemId: number) {
		this.loading = true;
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.loading = false;
					this.item = res.response;
					this.images = JSON.parse(this.item.imageURLs);
					this.purchaseOrder = this.item.poURL == null || this.item.poURL == "" ? new SupportingDocumentFileLineData() : JSON.parse(this.item.poURL);
					this.getCriticalSpares();
					this.getEvents();
					this.getProductionToolParts();
					this.getProjects();
					this.getBillets();
					this.getRequestForQuotes();
					this.getBomItems();
				},
				(err) => {
					this.loading = false;console.log(err); ToastApiError(this.toastService, err);}
			);
	}

	getMaterialName(materialId?: number | null): string {
		if (!materialId) return "";
		const m = this.materialPurchasableItems.find((x) => x.id === materialId);
		return m ? m.name : "";
	}

	// The part list Ctrl+K already holds in memory. It is empty for a moment straight after login,
	// so wait for the first load to land; take(1) then completes the subscription on its own.
	getParts() {
		this.partService.allParts
			.pipe(filter((parts) => parts.length > 0), take(1))
			.subscribe((parts) => {
				this.parts = [...parts].sort((a: any, b: any) => (a.partNumber || "").localeCompare(b.partNumber || ""));
			});
	}

	getProcesses() {
		this.processService.getAll()
			.subscribe(
				(res: any) => this.processes = res.response.items || [],
				(err) => console.log(err)
			);
	}

	getProductionToolParts() {
		this.productionToolPartService.forProductionToolId(this.item.id)
			.subscribe(
				(res: any) => {
					this.productionToolParts = res.response;
					// First linked part (earliest link) — shown read-only on the Details tab.
					const sorted = [...(this.productionToolParts || [])].sort((a: any, b: any) => a.id - b.id);
					this.firstPartNumber = sorted.length > 0 && sorted[0].part ? (sorted[0].part.partNumber || "") : "";
					this.firstPartImageUrl = sorted.length > 0 && sorted[0].part ? (sorted[0].part.tileImageUrl || "") : "";
				},
				(err) => console.log(err)
			);
	}

	getProjects() {
		if (!this.item.clientId) {
			this.projects = [];
			return;
		}
		this.projectService.getProjectsByClient(this.item.clientId)
			.subscribe(
				(res: any) =>  {
					this.projects = res.response;
					this.projects.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}
	
	getPurchasableItems() {		
		this.purchasableItemService.getAll()
			.subscribe(
				(res: any) => {
					const allItems: PurchasableItem[] = res.response.items;
					this.materialPurchasableItems = allItems.filter((pi) => pi.purchaseType === PurchaseTypeEnum.Material);
					// Two different lists off the same fetch: critical spares are machine spares, while a tool's BOM is
					// built from tooling accessories.
					this.purchasableItems = allItems.filter((pi) => pi.purchaseType === PurchaseTypeEnum.CriticalSpares);
					this.purchasableItems.sort((a,b) => a.name.localeCompare(b.name));
					this.bomPurchasableItems = allItems.filter((pi) => pi.purchaseType === PurchaseTypeEnum.ToolingAccessories);
					this.bomPurchasableItems.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getRequestForQuotes() {
		if (!this.item?.id) return;
		this.requestForQuoteService.getForProductionTool(this.item.id)
			.subscribe(
				(res: any) => this.requestForQuotes = res.response || [],
				(err) => console.log(err)
			);
	}

	// Only suppliers set up for tooling can be quoted, so only they are offered.
	getRfqSuppliers() {
		forkJoin([
			this.supplierPurchaseTypeService.getSuppliersForPurchaseType(PurchaseTypeEnum.ToolingBillet),
			this.supplierPurchaseTypeService.getSuppliersForPurchaseType(PurchaseTypeEnum.ToolingAccessories)
		]).subscribe(
			(results: any[]) => {
				const suppliers: Supplier[] = [];
				results.forEach((res: any) => {
					(res.response || []).forEach((row: any) => {
						const supplier = row.supplier || row;
						if (supplier && supplier.excludeFromRFQs !== true && suppliers.some((s) => s.id === supplier.id) === false) {
							suppliers.push(supplier);
						}
					});
				});
				this.rfqSuppliers = suppliers.sort((a, b) => a.name.localeCompare(b.name));
			},
			(err) => console.log(err)
		);
	}


	getSites() {
		this.siteService.getAll()
			.subscribe(
				(res: any) => {
					this.sites = res.response.items;
				},
				(err) => {console.log(err); ToastApiError(this.toastService, err);}
			);
	}

	getSuppliers() {		
		this.supplierService.getAll()
			.subscribe(
				(res: any) => {
					this.suppliers = res.response.items; 
					this.suppliers.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getToolingTypes() {
		this.toolingTypeService.active()
			.subscribe(
				(res: any) => {
					this.toolingTypes = res.response;
				},
				(err) => {console.log(err); ToastApiError(this.toastService, err);}
			);
	}

	goToToolOrderPlanning() {
		this.router.navigate(["/admin/toolOrderPlanning"]);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	mainFormDataChanged(args: any) {
		switch (args.dataField) {
			case "clientId":
				{
					if (args.value != undefined && args.value != null) {
						this.getProjects();
					}
				}
				break;
			case "toolWeightLower":
			case "toolWeightUpper":
				this.item.totalToolWeight = this.item.toolWeightLower + this.item.toolWeightUpper;
				break;
			case "toolHeight":
			case "toolLength":
			case "toolWidth":
				this.item.totalStorageVolume = this.item.toolHeight * this.item.toolLength * this.item.toolWidth;
				break;
		}
	}

	// The stages on the part's own router, so the tool is attached to a stage that exists.
	// Raise the quotes: one per chosen supplier per type they cover.
	requestQuotes() {
		if (this.popupRequestQuotes.supplierIds.length === 0) {
			this.toastService.error("Choose at least one supplier");
			return;
		}

		if (this.popupRequestQuotes.bomItemIds.length === 0 && this.popupRequestQuotes.toolOrderIds.length === 0) {
			this.toastService.error("Choose at least one billet or BOM item");
			return;
		}

		this.requestForQuoteService.createForProductionTool({ ...this.popupRequestQuotes, productionToolId: this.item.id })
			.subscribe(
				(res: any) => {
					const created = res.response ? res.response.created : 0;
					this.popupVisible = false;
					this.toastService.success(`${created} ${created === 1 ? "quote" : "quotes"} raised`);
					this.getRequestForQuotes();
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Could not raise the quotes."); }
			);
	}

	requestQuotesPopup() {
		// Everything on the tool is ticked to start with - it is quicker to remove a line than to find them all.
		this.popupRequestQuotes = {
			bomItemIds: (this.bomItems || []).map((b: any) => b.id),
			supplierIds: [],
			toolOrderIds: (this.billets || []).map((b: any) => b.id)
		};
		this.popupTitle = "Request Quotes";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.requestQuotes(), text: "Request Quotes", visible: true };
	}

	routerStagesForPart(partId: number | null): any[] {
		const part: any = (this.parts || []).find((p: any) => p.id === partId);

		if (!part || !part.router) {
			return [];
		}

		try {
			const router = JSON.parse(part.router) || [];
			return router
				.filter((r: any) => r && r.stage > 0)
				.sort((a: any, b: any) => a.stage - b.stage)
				.map((r: any) => {
					const process: any = (this.processes || []).find((p: any) => p.id === r.processId);
					return { id: r.stage, name: `OP${r.stage}0${process ? " - " + process.name : ""}` };
				});
		} catch (e) {
			console.log("Could not read the router for part " + partId, e);
			return [];
		}
	}

	saveHeader() {
		if (!this.popupTool.name) {
			alert("Enter a tool name");
			return;
		}
		this.itemService.updateSingleById(this.item.id, this.popupTool)
			.subscribe(
				() => {
					this.closePopup();
					this.getItem(this.item.id);
					this.toastService.success("Tool details updated");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Could not update the tool details.");
				}
			);
	}

	stageToOP(cellInfo: any) {
		if (cellInfo.value === null || cellInfo.value === undefined) {
			return "None";
		}
		return "OP" + cellInfo.value + "0";
	}
	
	updateCriticalSpare() {
		this.criticalSpareService.updateSingleById(this.popupCriticalSpare.id, this.popupCriticalSpare)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getCriticalSpares();
					this.toastService.success("Successfully Edited Critical Spare");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				} 
			);
	}

	updateEvent() {
		this.productionToolEventService.updateSingleById(this.popupEvent.id, this.popupEvent)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupEvent = new ProductionToolEvent();
					this.getEvents();
					this.toastService.success("Successfully Edited Event");
				},
				(err: any) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				} 
			);
	}

	updateItem() {
		this.item.imageURLs = JSON.stringify(this.images);
		this.item.poURL = JSON.stringify(this.purchaseOrder);
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => this.toastService.success("Successfully Updated "),
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				}
			);
	}

	uploadEventImage(file: File) {
		const containerName = "images";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		uploadData.append("mimeTypeKey", "ImageMimeTypes");
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					const eventImageUrl = `${environment.server.storageUrl}/${containerName}/${res.response[0].location}`;
					this.popupEvent.eventImageURL = eventImageUrl;
					this.toastService.success("Image uploaded - Click Save to confirm change");
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Image Upload Failed - Please ensure you are uploading a valid image file");
				}
			);
	}

	uploadImage(file: File) {
		const containerName = "images";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		uploadData.append("mimeTypeKey", "ImageMimeTypes");
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupImage.lookupId = res.response[0].lookupId;
					this.popupImage.fileName = res.response[0].name;
					this.popupImage.fileUrl = `${environment.server.storageUrl}/${containerName}/${res.response[0].location}`;
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Image Upload Failed - Please ensure you are uploading a valid image file");
				}
			);
	}

	uploadPurchaseOrder(file: File) {
		const containerName = "purchaseorders";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.purchaseOrder.lookupId = res.response[0].lookupId;
					this.purchaseOrder.fileName = res.response[0].name;
					this.purchaseOrder.fileUrl = `${environment.server.storageUrl}/${containerName}/${res.response[0].location}`;
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err, "Image Upload Failed - Please ensure you are uploading a valid image file");
				}
			);
	}

	viewBillet(e: any) {
		this.viewBilletById(e.row.data.id);
	}

	viewBilletById(billetId: number) {
		this.router.navigate([`/admin/toolOrders/${billetId}`]);
	}

	viewEventImage(e: any) {
		const imageUrl = e.row.data.eventImageURL;
		const viewLink = document.createElement("a");
		viewLink.href = imageUrl;
		document.body.appendChild(viewLink);
		viewLink.click();
		return false;
	}

	viewImage(e: any) {
		const imageUrl = e.row.data.fileUrl;
		const viewLink = document.createElement("a");
		viewLink.href = imageUrl;
		document.body.appendChild(viewLink);
		viewLink.click();
		return false;
	}

	viewPart(tp: any) {
		const partId = tp?.part?.id || tp?.partId;
		if (partId) this.router.navigate([`admin/parts/${partId}`]);
	}

	viewRequestForQuote(e: any) {
		window.open(`/admin/requestForQuotes/${e.row.data.lookupId}`, "_blank");
	}

	wizardBack() {
		if (this.wizardStep > 1) {
			this.wizardStep--;
		}
	}

	wizardDetailsValid(): boolean {
		if (!this.item.name) {
			this.toastService.error("Enter a tool name");
			return false;
		}
		if (!this.item.clientId) {
			this.toastService.error("Choose a client");
			return false;
		}
		if (!this.item.toolingTypeId) {
			this.toastService.error("Choose a tooling type");
			return false;
		}
		return true;
	}

	wizardNext() {
		if (this.wizardStep === 1 && !this.wizardPartValid()) {
			return;
		}
		if (this.wizardStep === 2 && !this.wizardDetailsValid()) {
			return;
		}
		if (this.wizardStep < 3) {
			this.wizardStep++;
		}
	}

	wizardPartChanged(partId: number) {
		this.wizardPartId = partId;
		this.wizardRouterStages = this.routerStagesForPart(partId);
		// Default to the part's first stage; a part with no router falls back to a typed stage number.
		this.wizardPartRouterStage = this.wizardRouterStages.length > 0 ? this.wizardRouterStages[0].id : 1;
		// Fill the name from the part unless it has been typed over.
		if (!this.item.name || this.item.name === this.wizardLastSuggestedName) {
			this.item.name = this.generatedToolName();
			this.wizardLastSuggestedName = this.item.name;
		}

		// The tool belongs to whoever the part belongs to, so carry the client and project over.
		// Changing the part re-fills both: the pair has to stay consistent with the part.
		const part: any = (this.parts || []).find((p: any) => p.id === partId);

		if (part) {
			this.item.clientId = part.clientId;
			this.item.projectId = part.projectId;
			// Projects are loaded per client, so the list has to follow the client or the dropdown is blank.
			this.getProjects();
		}
	}

	wizardPartValid(): boolean {
		if (!this.wizardPartId) {
			this.toastService.error("Choose the part this tool makes");
			return false;
		}
		if (!this.wizardPartRouterStage || this.wizardPartRouterStage < 1) {
			this.toastService.error("Enter the router stage this tool is used for");
			return false;
		}
		return true;
	}

}